import { Insurance, PatientPersonalInfo } from "patients-experience-client";
import React, { useEffect, useState } from "react";
import { createPatientApi } from "../../../../api-helpers/PatientApiHelper";
import OffCanvas from "../../../../components/offCanvas";
import { AddUpdateInsurance } from "./AddUpdateInsurance";
import { formatDate, formatDateLong } from "../../../../helper/dateFormatter";
import Toaster, { ToastConfig } from "../../../../components/Toaster";
import { ErrorTypes } from "../../../../interfaces/errors";
import Spinner from "../../../../components/Spinner";
import { ReactComponent as EditIcon } from "../../../../assets/img/EditPatientInfo.svg";
import ConfirmationBox from "../../../../components/ConfirmationBox";

interface InsuranceProps {
    patientInfo: PatientPersonalInfo | undefined;
}

export const PatientInsurance: React.FC<InsuranceProps> = (props: InsuranceProps) => {
    const patientApi = createPatientApi();
    const [communicationEdit, setCommunicationEdit] = useState(false);
    const [patientInsurace, setPatientInsurace] = useState<Insurance[]>();
    const [offCanvasTitle, setOffCanvasTitle] = useState("");
    const [offCanvasShow, setOffCanvasShow] = useState(false);
    const [loading, setLoading] = useState(true);
    const [offCanvasComp, setOffCanvasComp] = useState("");
    const [showToast, setShowToast] = useState(false);
    const [selectedInsurance, setSelectedInsurance] = useState<Insurance>();
    const [showConfirmBox, setShowConfirmBox] = useState(false);
    const [confirmTitle, setConfirmTitle] = useState("");
    const [confirmMessage, setConfirmMessage] = useState("");

    const [toastConfig, setToastConfig] = useState<ToastConfig>({
        type: "success" as ErrorTypes,
        title: "",
        icon: "",
    });

    const getInsurace = async () => {
        setLoading(true);
        try {
            const response = await patientApi.patientsControllerGetInsurance(props.patientInfo!.patientId);
            setPatientInsurace(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

      // Function to delete insurance
  const deleteInsurance = async (insurance: Insurance) => {
    setShowConfirmBox(true);
    setConfirmTitle("Delete Insurance");
    setConfirmMessage(
      `Are you sure you want to delete ${insurance?.policyHolderName}'s insurance'? This action cannot be undone.`
    );
    setSelectedInsurance(insurance);
  };

    // Function to handle confirmation of deletion
    const handleDeleteInsurance = async () => {
        if (!selectedInsurance) return;
        setLoading(true);
        try {
          await patientApi.patientsControllerDeleteInsurance(
            props.patientInfo!.patientId,
            selectedInsurance.id
          );
          setToastConfig({
            type: "success",
            title: "Insurance has been successfully deleted.",
            icon: "check_circle",
          });
          setShowToast(true);
          getInsurace(); // Refresh insurance list
        } catch (error) {
          console.error("Error while deleting insurance:", error); // Log the error
          setToastConfig({
            type: "danger",
            title: "Unable to delete insurance. Please try again.",
            icon: "error",
          });
          setShowToast(true);
        } finally {
          setLoading(false);
          setShowConfirmBox(false);
        }
      };

    const onSubmit = async (toasterConfig: ToastConfig) => {
        setToastConfig(toasterConfig);
        setShowToast(true);
        getInsurace();
    };

    useEffect(() => {
        if (props.patientInfo?.patientId) {
            getInsurace();
        }
    }, [props.patientInfo]);

    const handleOffCanvasShow = (comp: string, insurance?: Insurance) => {
        if(insurance) {
            setOffCanvasTitle("Edit Insurance");
            setSelectedInsurance(insurance);
        } else {
            setOffCanvasTitle("Add Insurance");
            setSelectedInsurance(undefined);
        }
        setOffCanvasComp(comp);
        setOffCanvasShow(!offCanvasShow);
    };

    return (
        <>
        {loading && <Spinner />}
        {showConfirmBox && (
        <ConfirmationBox
          showConfirmBox={showConfirmBox}
          closeConfirmBox={() => setShowConfirmBox(false)}
          performAction={handleDeleteInsurance}
          message={confirmMessage}
          title={""}
        />
      )}
     <div className="row">
            <Toaster
                show={showToast}
                onClose={() => setShowToast(false)}
                type={toastConfig.type}
                title={toastConfig.title}
                message={toastConfig.message}
                icon={toastConfig.icon}
            />
        </div>
            <h2 className='h4 m-0'>Insurance</h2>
            <hr />
            <div className="row g-2">
                {patientInsurace && patientInsurace.map((insurance: Insurance, index: number) => (<>
                {index > 0 && <><br></br></>}
                    <div className="col-12 col-md-12 col-lg-12 col-xl-6 px-5 mb-4">
                        <div className="row">
                            <div className="col-12 mb-2">
                                <div className="d-flex align-items-center">
                                    <p className="fw-bold fs-15 flex-fill">{insurance.insurancePreference === 'P' ? 'Primary Insurance Policy' : insurance.insurancePreference === 'S' ? 'Secondary Insurance Policy' : 'Other Insurance Policy'}</p>

                                    {/* Delete Button */}
                                    <div className="d-flex">
                                            {insurance.insurancePreference === 'O' && (
                                                <a
                                                    href="#"
                                                    onClick={() => {
                                                    deleteInsurance(insurance);
                                                    }}
                                                    className="d-flex align-items-center py-1 ms-2"
                                                >
                                                    <span className="material-symbols-outlined fs-22 align-text-top ms-2">
                                                    delete
                                                    </span>
                                                </a>)}
                                                <a href='javascript:void(0)' className={`btn d-flex align-items-center py-1 pe-4 ms-auto`} onClick={() => handleOffCanvasShow("editInsurance", insurance)}>
                                                    <EditIcon />
                                                    <span className="d-inline-block lh-sm ms-2 border-primary border-bottom text-secondary">Edit</span>
                                                </a>
                                    </div>

                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 col-xl-4">
                                <div className="mb-2">
                                    <label
                                        className="form-label text-light mb-1 fs-14"
                                    >
                                        Policy holder
                                    </label>
                                    <p className="fw-medium mb-0 fs-14">{insurance.policyHolderName ? insurance.policyHolderName : '-'}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 col-xl-4">
                                <div className="mb-2">
                                    <label
                                        className="form-label text-light mb-1 fs-14"
                                    >
                                        Relation
                                    </label>
                                    <p className="fw-medium mb-0 fs-14">{insurance.relation ? insurance.relation : '-'}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 col-xl-4">
                                <div className="mb-2">
                                    <label
                                        className="form-label text-light mb-1 fs-14"
                                    >
                                        Policy holder's DOB
                                    </label>
                                    <p className="fw-medium mb-0 fs-14">{insurance.policyHolderDOB ? formatDate(insurance.policyHolderDOB) : '-'}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 col-xl-4">
                                <div className="mb-2">
                                    <label
                                        className="form-label text-light mb-1 fs-14"
                                    >
                                        Insurance ID
                                    </label>
                                    <p className="fw-medium mb-0 fs-14">{insurance.insuranceId ? insurance.insuranceId : '-'}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 col-xl-4">
                                <div className="mb-2">
                                    <label
                                        className="form-label text-light mb-1 fs-14"
                                    >
                                        Group Number
                                    </label>
                                    <p className="fw-medium mb-0 fs-14">{insurance.groupNumber ? insurance.groupNumber : '-'}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 col-xl-4">
                                <div className="mb-2">
                                    <label
                                        className="form-label text-light mb-1 fs-14"
                                    >
                                        Insurance Provider
                                    </label>
                                    <p className="fw-medium mb-0 fs-14">{insurance.insuranceProvider ? insurance.insuranceProvider : '-'}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 col-xl-4">
                                <div className="mb-2">
                                    <label
                                        className="form-label text-light mb-1 fs-14"
                                    >
                                        Type
                                    </label>
                                    {/* @TODO: Type name instead of typeId */}
                                    <p className="fw-medium mb-0 fs-14">{insurance.insuranceType?.name ? insurance.insuranceType?.name : '-'}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 col-xl-4">
                                <div className="mb-2">
                                    <label
                                        className="form-label text-light mb-1 fs-14"
                                    >
                                        Start Date
                                    </label>
                                    <p className="fw-medium mb-0 fs-14">{insurance.startDate ? formatDate(insurance.startDate) : '-'}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 col-xl-4">
                                <div className="mb-2">
                                    <label
                                        className="form-label text-light mb-1 fs-14"
                                    >
                                        End Date
                                    </label>
                                    <p className="fw-medium mb-0 fs-14">{insurance.endDate ? formatDate(insurance.endDate) : '-'}</p>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4 col-xl-4">
                                <div className="mb-2">
                                    <label
                                        className="form-label text-light mb-1 fs-14"
                                    >
                                        Insurance Preference
                                    </label>
                                    <p className="fw-medium mb-0 fs-14">
                                        {insurance.insurancePreference === 'P' ? 'Primary' : insurance.insurancePreference === 'S' ? 'Secondary' : 'Other'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </>))}
            </div>
            <a href='javascript:void(0)' className={`d-inline-block mb-4 mt-4 border-primary border-bottom`} onClick={() => handleOffCanvasShow("addInsurance")}>
                <span className="lh-sm">Add</span>
                <span className="material-symbols-outlined fs-16 align-text-top me-0">add</span>
            </a>

            <OffCanvas
                title={offCanvasTitle}
                offCanvasShow={offCanvasShow}
                setOffCanvasShow={setOffCanvasShow}
                offCanvasComp={offCanvasComp}
            >
                <AddUpdateInsurance patientInfo={props.patientInfo} onSubmit={onSubmit} selectedInsurance={selectedInsurance} setOffCanvasShow={setOffCanvasShow} />
            </OffCanvas>
        </>
    );
}
